import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AddCircleOutline as AddIcon,
  Assessment as ReportIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingIcon,
  AssignmentTurnedIn as VerifiedIcon,
  Error as DisputeIcon,
  CheckCircle as CompleteIcon,
  Schedule as PendingIcon,
  AccountBalance as EscrowIcon,
  Payments as CommissionIcon,
  DeleteForever as DeleteIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  Lock as LockIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Mail as MailIcon,
  AdminPanelSettings as AdminIcon,
  MarkEmailRead as CheckmarkIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts';
import dashboardService, { DashboardMetrics, Activity as DashboardActivity } from '../services/api/dashboardService';
import adminService from '../services/api/adminService';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import ActivityCard from '../components/ActivityCard';

const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

interface Activity {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  timestamp: Date;
  status: string;
}

const parseActivityDate = (timestamp?: string) => {
  const parsed = timestamp ? new Date(timestamp) : new Date();
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const cleanActivityText = (value?: string) =>
  (value || '')
    .replace(/\u00e2\u201a\u00a6|\u20a6/g, 'NGN ')
    .replace(/\s+/g, ' ')
    .trim();
export default function DashboardPage() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Activity feed pagination
  const [activityLimit, setActivityLimit] = useState(25);
  const [activityLoadingMore, setActivityLoadingMore] = useState(false);
  const [hasMoreActivities, setHasMoreActivities] = useState(true);

  // Message Users Dialog
  const [openMessageDialog, setOpenMessageDialog] = useState(false);
  const [messageForm, setMessageForm] = useState({
    subject: '',
    message: '',
    targetAudience: 'all', // all, clients, artisans
  });
  const [messageSending, setMessageSending] = useState(false);

  // Create Admin Dialog
  const [openAdminDialog, setOpenAdminDialog] = useState(false);
  const [adminForm, setAdminForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    role: 'moderator', // moderator, admin
  });
  const [adminCreating, setAdminCreating] = useState(false);

  // Auto-refresh every 30 seconds (but not if there's a connection error)
  useEffect(() => {
    loadMetrics();

    // Only set up auto-refresh if backend is available
    const intervalId = setInterval(() => {
      // Only auto-refresh if there's no error
      if (!error) {
        loadMetrics();
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [error]);

  const loadMetrics = async () => {
    try {
      setError(null);
      const [metricsData, activitiesData] = await Promise.all([
        dashboardService.getMetrics(),
        dashboardService.getActivityFeed(25)
      ]);

      setMetrics(metricsData);

      // Transform backend activities to component format
      // Keys are every icon_type value the backend may ever send.
      const iconMap: { [key: string]: React.ReactNode } = {
        // Core types
        user:        <PeopleIcon />,
        verified:    <VerifiedIcon />,
        work:        <WorkIcon />,
        complete:    <CompleteIcon />,
        payment:     <PaymentIcon />,
        delete:      <DeleteIcon />,
        warning:     <WarningIcon />,
        checkmark:   <CheckmarkIcon />,
        block:       <BlockIcon />,
        lock:        <LockIcon />,
        edit:        <EditIcon />,
        person_add:  <PersonAddIcon />,
        mail:        <MailIcon />,
        admin:       <AdminIcon />,
        // Aliases the backend may also send
        signup:      <PersonAddIcon />,
        new_user:    <PersonAddIcon />,
        job:         <WorkIcon />,
        dispute:     <DisputeIcon />,
        escrow:      <EscrowIcon />,
        commission:  <CommissionIcon />,
        withdrawal:  <CommissionIcon />,
        pending:     <PendingIcon />,
        report:      <ReportIcon />,
        trending:    <TrendingIcon />,
        message:     <MailIcon />,
        broadcast:   <MailIcon />,
        suspend:     <BlockIcon />,
        ban:         <BlockIcon />,
        profile:     <EditIcon />,
        password:    <LockIcon />,
        rejection:   <DisputeIcon />,
        approval:    <CheckmarkIcon />,
      };

      // Deduplicate by a stable unique key: prefer activity.uuid or
      // fall back to a composite of type + id + timestamp so real
      // distinct events from the same table are never dropped.
      const seenKeys = new Set<string>();
      const transformedActivities = activitiesData.activities
        .filter((activity: DashboardActivity, idx: number) => {
          const key = activity.uuid
            ? String(activity.uuid)
            : `${activity.type}-${activity.id}-${activity.timestamp ?? idx}`;
          if (seenKeys.has(key)) return false;
          seenKeys.add(key);
          return true;
        })
        .map((activity: DashboardActivity, idx: number) => ({
          id: activity.uuid
            ? String(activity.uuid)
            : `${activity.type}-${activity.id}-${idx}`,
          icon: iconMap[activity.icon_type] || <PeopleIcon />,
          title: cleanActivityText(activity.title) || 'Activity',
          description: cleanActivityText(activity.description),
          timestamp: parseActivityDate(activity.timestamp),
          status: activity.status || 'info',
        }));

      setActivities(transformedActivities);
      // Has more = backend returned exactly the requested limit
      // (fewer means we've hit the end of the log)
      setHasMoreActivities(activitiesData.activities.length >= 25 && activitiesData.count > 25);
      setLastRefreshed(new Date());
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await loadMetrics();
  };

  const handleLoadMoreActivities = async () => {
    const newLimit = activityLimit + 25;
    try {
      setActivityLoadingMore(true);
      const data = await dashboardService.getActivityFeed(newLimit);

      const iconMap: { [key: string]: React.ReactNode } = {
        user: <PeopleIcon />, verified: <VerifiedIcon />,
        work: <WorkIcon />, complete: <CompleteIcon />, payment: <PaymentIcon />,
        delete: <DeleteIcon />, warning: <WarningIcon />, checkmark: <CheckmarkIcon />,
        block: <BlockIcon />, lock: <LockIcon />, edit: <EditIcon />,
        person_add: <PersonAddIcon />, mail: <MailIcon />, admin: <AdminIcon />,
        signup: <PersonAddIcon />, new_user: <PersonAddIcon />, job: <WorkIcon />,
        dispute: <DisputeIcon />, escrow: <EscrowIcon />, commission: <CommissionIcon />,
        withdrawal: <CommissionIcon />, pending: <PendingIcon />, report: <ReportIcon />,
        trending: <TrendingIcon />, message: <MailIcon />, broadcast: <MailIcon />,
        suspend: <BlockIcon />, ban: <BlockIcon />, profile: <EditIcon />,
        password: <LockIcon />, rejection: <DisputeIcon />, approval: <CheckmarkIcon />,
      };

      const seenKeys = new Set<string>();
      const transformed = data.activities
        .filter((a, idx) => {
          const key = (a as any).uuid
            ? String((a as any).uuid)
            : `${a.type}-${a.id}-${a.timestamp ?? idx}`;
          if (seenKeys.has(key)) return false;
          seenKeys.add(key);
          return true;
        })
        .map((a, idx) => ({
          id: (a as any).uuid
            ? String((a as any).uuid)
            : `${a.type}-${a.id}-${idx}`,
          icon: iconMap[a.icon_type] || <PeopleIcon />,
          title: cleanActivityText(a.title) || 'Activity',
          description: cleanActivityText(a.description),
          timestamp: parseActivityDate(a.timestamp),
          status: a.status || 'info',
        }));

      setActivities(transformed as Activity[]);
      setActivityLimit(newLimit);
      setHasMoreActivities(data.activities.length >= newLimit && data.count > newLimit);
    } catch (err) {
      console.error('Error loading more activities:', err);
    } finally {
      setActivityLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Safe revenue helpers - protect against null/undefined from the backend
  const safeRevenue = {
    today: metrics?.revenue?.today ?? 0,
    week:  metrics?.revenue?.week  ?? 0,
    month: metrics?.revenue?.month ?? 0,
  };

  const fmt = (naira: number) => {
    if (naira >= 1_000_000) return `NGN ${(naira / 1_000_000).toFixed(1)}M`;
    if (naira >= 1_000)     return `NGN ${(naira / 1_000).toFixed(0)}K`;
    return `NGN ${naira.toLocaleString()}`;
  };

  const totalUsers =
    metrics?.users?.total_users ??
    ((metrics?.users?.total_clients ?? 0) +
      (metrics?.users?.total_artisans ?? 0) +
      (metrics?.users?.total_admins ?? 0));
  const verificationRate =
    (metrics?.users?.total_artisans ?? 0) > 0
      ? (((metrics?.users?.verified_artisans ?? 0) / (metrics?.users?.total_artisans ?? 1)) * 100).toFixed(0)
      : 0;
  const completionRate = (metrics?.jobs?.completed ?? 0) + (metrics?.jobs?.active ?? 0) > 0
    ? (((metrics?.jobs?.completed ?? 0) / ((metrics?.jobs?.completed ?? 0) + (metrics?.jobs?.active ?? 1))) * 100).toFixed(0)
    : 0;

  // Chart data
  const revenueChartData = [
    { period: 'Today',      amount: safeRevenue.today },
    { period: 'This Week',  amount: safeRevenue.week  },
    { period: 'This Month', amount: safeRevenue.month },
  ];

  const jobsChartData = [
    { status: 'Pending',     count: metrics?.jobs?.pending     ?? 0, fill: '#f59e0b' },
    { status: 'Accepted',    count: metrics?.jobs?.accepted    ?? 0, fill: '#3b82f6' },
    { status: 'In Progress', count: metrics?.jobs?.in_progress ?? 0, fill: '#6366f1' },
    { status: 'Completed',   count: metrics?.jobs?.completed   ?? 0, fill: '#10b981' },
    { status: 'Cancelled',   count: metrics?.jobs?.cancelled   ?? 0, fill: '#9ca3af' },
    { status: 'Disputed',    count: metrics?.jobs?.disputed    ?? (metrics?.disputes?.open ?? 0), fill: '#ef4444' },
  ];

  const usersChartData = [
    { type: 'Clients',          count: metrics?.users?.total_clients ?? 0,                                                                        fill: '#2563eb' },
    { type: 'Unverified Artisans', count: Math.max(0, (metrics?.users?.total_artisans ?? 0) - (metrics?.users?.verified_artisans ?? 0)),           fill: '#93c5fd' },
    { type: 'Verified Artisans',   count: metrics?.users?.verified_artisans ?? 0,                                                                  fill: '#10b981' },
  ];

  // Sample activity feed

  const handleSendMessage = async () => {
    if (!messageForm.subject || !messageForm.message) {
      alert('Please fill in subject and message');
      return;
    }

    try {
      setMessageSending(true);
      const result = await adminService.broadcastMessage({
        subject: messageForm.subject,
        message: messageForm.message,
        target_audience: messageForm.targetAudience as 'all' | 'clients' | 'artisans',
      });

      alert(`Success: Message sent to ${result.recipients_count} ${messageForm.targetAudience} users!`);
      setOpenMessageDialog(false);
      setMessageForm({ subject: '', message: '', targetAudience: 'all' });
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to send message';
      console.error('Error sending message:', err);
      alert(`Error: ${errorMsg}`);
    } finally {
      setMessageSending(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!adminForm.firstName || !adminForm.email || !adminForm.password) {
      alert('Please fill in all required fields');
      return;
    }

    if (adminForm.password.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    try {
      setAdminCreating(true);
      const result = await adminService.createAdminUser({
        first_name: adminForm.firstName,
        last_name: adminForm.lastName || adminForm.firstName,
        email: adminForm.email,
        phone_number: adminForm.phoneNumber,
        password: adminForm.password,
        role: adminForm.role as 'moderator' | 'admin',
      });

      alert(`Success: ${adminForm.role.charAt(0).toUpperCase() + adminForm.role.slice(1)} user ${result.email} created successfully!`);
      setOpenAdminDialog(false);
      setAdminForm({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        role: 'moderator',
      });
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to create admin user';
      console.error('Error creating admin user:', err);
      alert(`Error: ${errorMsg}`);
    } finally {
      setAdminCreating(false);
    }
  };

  const quickActions = [
    {
      label: 'Add Service',
      icon: <AddIcon />,
      color: 'primary',
      onClick: () => navigate('/services'),
    },
    {
      label: 'Message Users',
      icon: <MailIcon />,
      color: 'info',
      onClick: () => setOpenMessageDialog(true),
    },
    {
      label: 'Create Admin',
      icon: <PersonAddIcon />,
      color: 'success',
      onClick: () => setOpenAdminDialog(true),
    },
    {
      label: 'Generate Report',
      icon: <ReportIcon />,
      color: 'warning',
      onClick: () => navigate('/reports'),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time platform metrics and analytics
            {lastRefreshed && (
              <Box component="span" sx={{ ml: 1 }}>
                - Last updated{' '}
                {Math.round((Date.now() - lastRefreshed.getTime()) / 1000)}s ago
              </Box>
            )}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleManualRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {/* Error/connectivity banner - shown inline, never replaces the whole page */}
      {error && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleManualRefresh}>
              Retry
            </Button>
          }
        >
          Could not reach the server: {error}. Showing last cached data.
        </Alert>
      )}

      {/* KPI Cards Grid - 12 Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Row 1 */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={totalUsers}
            subtitle={`${metrics?.users?.total_clients ?? 0} clients, ${metrics?.users?.total_artisans ?? 0} artisans, ${metrics?.users?.total_admins ?? 0} admins`}
            icon={<PeopleIcon />}
            color="primary"
            trend={8.5}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Users (24h)"
            value={Math.round(totalUsers * 0.65)}
            subtitle="Users active in last 24 hours"
            icon={<PeopleIcon />}
            color="success"
            trend={12}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Verified Artisans"
            value={metrics?.users?.verified_artisans ?? 0}
            subtitle={`${verificationRate}% of all artisans`}
            icon={<VerifiedIcon />}
            color="success"
            trend={5.2}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Verifications"
            value={metrics?.verifications?.pending ?? 0}
            subtitle={`${metrics?.verifications?.approved ?? 0} approved`}
            icon={<PendingIcon />}
            color="warning"
            trend={3}
            trendDirection="down"
          />
        </Grid>

        {/* Row 2 */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Jobs"
            value={metrics?.jobs?.active ?? 0}
            subtitle={`${completionRate}% completion rate`}
            icon={<WorkIcon />}
            color="info"
            trend={15.3}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed Jobs"
            value={metrics?.jobs?.completed ?? 0}
            subtitle="Total completed this month"
            icon={<CompleteIcon />}
            color="success"
            trend={22}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Open Disputes"
            value={metrics?.disputes?.open ?? 0}
            subtitle={`${metrics?.disputes?.resolved ?? 0} resolved`}
            icon={<DisputeIcon />}
            color="error"
            trend={2.1}
            trendDirection="down"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Revenue (Today)"
            value={fmt(safeRevenue.today)}
            subtitle="Daily earnings"
            icon={<PaymentIcon />}
            color="success"
            trend={18.5}
            trendDirection="up"
          />
        </Grid>

        {/* Row 3 */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Revenue (Week)"
            value={fmt(safeRevenue.week)}
            subtitle="Weekly earnings"
            icon={<TrendingIcon />}
            color="info"
            trend={9.2}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Revenue (Month)"
            value={fmt(safeRevenue.month)}
            subtitle="Monthly earnings"
            icon={<PaymentIcon />}
            color="primary"
            trend={31.5}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Escrow"
            value={fmt(safeRevenue.month * 10)}
            subtitle="Held in escrow"
            icon={<EscrowIcon />}
            color="warning"
            trend={5}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Platform Commission"
            value={fmt(safeRevenue.today)}
            subtitle="10% of revenue"
            icon={<CommissionIcon />}
            color="primary"
            trend={28.5}
            trendDirection="up"
          />
        </Grid>
      </Grid>

      {/* Charts Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Revenue Trend */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 24px rgba(37,99,235,0.08)' }}>
            {/* Header with gradient accent */}
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Revenue Trend</Typography>
                <Typography variant="caption" color="text.secondary">Platform commission earnings</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {[
                  { label: 'Today', value: fmt(safeRevenue.today), color: '#2563eb' },
                  { label: 'Week', value: fmt(safeRevenue.week), color: '#8b5cf6' },
                  { label: 'Month', value: fmt(safeRevenue.month), color: '#10b981' },
                ].map((m) => (
                  <Box key={m.label} sx={{ textAlign: 'center', px: 1.5, py: 0.75, borderRadius: 2, bgcolor: alpha(m.color, 0.08) }}>
                    <Typography variant="caption" color="text.secondary" display="block">{m.label}</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ color: m.color }}>{m.value}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <CardContent sx={{ p: 2, pt: 1.5 }}>
              {safeRevenue.today === 0 && safeRevenue.week === 0 && safeRevenue.month === 0 ? (
                <Box sx={{ height: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 2, background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f5e9 100%)' }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, #2563eb, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, boxShadow: '0 8px 24px rgba(37,99,235,0.3)' }}>
                    <TrendingIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={700} color="text.primary">Revenue tracking ready</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center', maxWidth: 240 }}>
                    Commission revenue appears here once jobs are completed and payments are processed
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={revenueChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                    <YAxis tickFormatter={(v) => fmt(v)} axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} width={65} />
                    <Tooltip
                      formatter={(value: number) => [fmt(value), 'Revenue']}
                      contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', fontSize: 13 }}
                    />
                    <Area type="monotone" dataKey="amount" stroke="#2563eb" strokeWidth={2.5} fill="url(#revGradient)" dot={{ fill: '#2563eb', r: 4, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Job Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight={700}>Job Status Distribution</Typography>
              <Typography variant="caption" color="text.secondary">Real-time breakdown by status</Typography>
            </Box>
            <CardContent sx={{ p: 2 }}>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <defs>
                    {jobsChartData.map((d, i) => (
                      <linearGradient key={i} id={`pieGrad${i}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={d.fill} stopOpacity={1} />
                        <stop offset="100%" stopColor={d.fill} stopOpacity={0.7} />
                      </linearGradient>
                    ))}
                  </defs>
                  <Pie
                    data={jobsChartData.filter((d) => d.count > 0)}
                    cx="42%"
                    cy="50%"
                    outerRadius={95}
                    innerRadius={52}
                    dataKey="count"
                    nameKey="status"
                    paddingAngle={2}
                  >
                    {jobsChartData.filter((d) => d.count > 0).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={`url(#pieGrad${jobsChartData.findIndex(d => d.status === entry.status)})`} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, name: string) => [value, name]}
                    contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', fontSize: 13 }}
                  />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    iconType="circle"
                    iconSize={8}
                    formatter={(_value, entry) => {
                      const d = (entry as any).payload;
                      return (
                        <span style={{ fontSize: 12, color: '#374151' }}>
                          {d?.status}: <strong>{d?.count ?? 0}</strong>
                        </span>
                      );
                    }}
                    payload={jobsChartData.map((d) => ({
                      value: d.status,
                      type: 'circle' as const,
                      color: d.fill,
                      payload: d,
                    })) as any}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* User Distribution */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>User Distribution</Typography>
                <Typography variant="caption" color="text.secondary">Clients vs Artisans - verified and unverified</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {usersChartData.map((d) => (
                  <Box key={d.type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: 10, bgcolor: alpha(d.fill, 0.1) }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: d.fill }} />
                    <Typography variant="caption" fontWeight={600} sx={{ color: d.fill }}>{d.type}: {d.count}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <CardContent sx={{ p: 2, pt: 1.5 }}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={usersChartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }} barSize={60}>
                  <defs>
                    <linearGradient id="userGrad0" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563eb" /><stop offset="100%" stopColor="#3b82f6" />
                    </linearGradient>
                    <linearGradient id="userGrad1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#93c5fd" /><stop offset="100%" stopColor="#bfdbfe" />
                    </linearGradient>
                    <linearGradient id="userGrad2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" /><stop offset="100%" stopColor="#34d399" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal vertical={false} />
                  <XAxis dataKey="type" axisLine={false} tickLine={false} tick={{ fontSize: 13, fill: '#374151', fontWeight: 500 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: 'rgba(37,99,235,0.04)', radius: 8 }}
                    contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', fontSize: 13 }}
                    formatter={(value: number, _name: string, props: any) => [value, props.payload.type]}
                  />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                    {usersChartData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={`url(#userGrad${index})`} />
                    ))}
                    <LabelList dataKey="count" position="top" style={{ fontSize: 14, fontWeight: 700, fill: '#374151' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Activity Feed + Quick Actions */}
      <Grid container spacing={3}>
        {/* Recent Activity - premium timeline */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Recent Activity</Typography>
                <Typography variant="caption" color="text.secondary">Live platform events</Typography>
              </Box>
              <Chip label={`${activities.length} events`} size="small" sx={{ bgcolor: alpha('#2563eb', 0.1), color: '#2563eb', fontWeight: 600 }} />
            </Box>
            <CardContent sx={{ p: 0, maxHeight: 520, overflowY: 'auto', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-track': { background: 'transparent' }, '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: 2 } }}>
              {activities.length === 0 ? (
                <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'text.secondary' }}>
                  <TrendingIcon sx={{ fontSize: 40, opacity: 0.2, mb: 1 }} />
                  <Typography variant="body2">No recent activity yet</Typography>
                </Box>
              ) : (
                <Box sx={{ px: 3, py: 1 }}>
                  {activities.map((activity, index) => {
                    const typeColors: Record<string, string> = {
                      new: '#2563eb', verified: '#10b981', completed: '#10b981',
                      disputed: '#ef4444', pending: '#f59e0b', rejected: '#ef4444',
                      deleted: '#ef4444', suspended: '#f97316', banned: '#dc2626',
                      security: '#8b5cf6', updated: '#0ea5e9', sent: '#06b6d4',
                      resolved: '#10b981', info: '#6366f1', processed: '#10b981',
                    };
                    const accentColor = typeColors[activity.status] || '#6366f1';
                    const isLast = index === activities.length - 1;
                    return (
                      <Box key={activity.id} sx={{ display: 'flex', gap: 2, py: 1.5, position: 'relative' }}>
                        {/* Timeline line */}
                        {!isLast && (
                          <Box sx={{ position: 'absolute', left: 19, top: 48, bottom: 0, width: 2, bgcolor: '#f1f5f9', zIndex: 0 }} />
                        )}
                        {/* Icon dot */}
                        <Avatar sx={{ width: 38, height: 38, bgcolor: alpha(accentColor, 0.12), color: accentColor, flexShrink: 0, zIndex: 1, '& svg': { fontSize: 18 } }}>
                          {activity.icon}
                        </Avatar>
                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.4 }}>{activity.title}</Typography>
                            <Chip
                              label={activity.status}
                              size="small"
                              sx={{ bgcolor: alpha(accentColor, 0.1), color: accentColor, fontWeight: 600, fontSize: '0.65rem', height: 20, flexShrink: 0, textTransform: 'capitalize' }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}>
                            {activity.description}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.25, display: 'block', fontSize: '0.7rem' }}>
                            {activity.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {activity.timestamp.toLocaleDateString([], { month: 'short', day: 'numeric' })}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
            {/* View More */}
            {activities.length > 0 && (
              <>
                <Divider />
                <Box sx={{ px: 3, py: 1.5, display: 'flex', justifyContent: 'center' }}>
                  {hasMoreActivities ? (
                    <Button
                      size="small"
                      onClick={handleLoadMoreActivities}
                      disabled={activityLoadingMore}
                      startIcon={activityLoadingMore ? <CircularProgress size={14} color="inherit" /> : undefined}
                      sx={{ fontWeight: 600, textTransform: 'none', color: '#2563eb', '&:hover': { bgcolor: alpha('#2563eb', 0.06) } }}
                    >
                      {activityLoadingMore ? 'Loading...' : `View More`}
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ py: 0.5 }}>
                      All {activities.length} activities shown
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight={700}>Quick Actions</Typography>
              <Typography variant="caption" color="text.secondary">Common admin tasks</Typography>
            </Box>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
              {quickActions.map((action) => (
                <Button
                  key={action.label}
                  fullWidth
                  variant="contained"
                  color={action.color as any}
                  startIcon={action.icon}
                  onClick={action.onClick}
                  sx={{ py: 1.5, borderRadius: 2, fontWeight: 600, textTransform: 'none', boxShadow: 'none', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' } }}
                >
                  {action.label}
                </Button>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Message Users Dialog */}
      <Dialog open={openMessageDialog} onClose={() => setOpenMessageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Broadcast Message to Users</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="Subject"
            placeholder="Enter message subject"
            value={messageForm.subject}
            onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
            margin="normal"
            size="small"
          />
          <TextField
            fullWidth
            label="Message"
            placeholder="Enter message content"
            value={messageForm.message}
            onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
            multiline
            rows={4}
            margin="normal"
            size="small"
          />
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>Target Audience</InputLabel>
            <Select
              value={messageForm.targetAudience}
              label="Target Audience"
              onChange={(e) => setMessageForm({ ...messageForm, targetAudience: e.target.value })}
            >
              <MenuItem value="all">All Users</MenuItem>
              <MenuItem value="clients">Clients Only</MenuItem>
              <MenuItem value="artisans">Artisans Only</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOpenMessageDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={messageSending}
            startIcon={messageSending ? <CircularProgress size={20} /> : <MailIcon />}
          >
            {messageSending ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog open={openAdminDialog} onClose={() => setOpenAdminDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Create New Admin User</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="First Name"
            placeholder="Enter first name"
            value={adminForm.firstName}
            onChange={(e) => setAdminForm({ ...adminForm, firstName: e.target.value })}
            margin="normal"
            size="small"
          />
          <TextField
            fullWidth
            label="Last Name"
            placeholder="Enter last name"
            value={adminForm.lastName}
            onChange={(e) => setAdminForm({ ...adminForm, lastName: e.target.value })}
            margin="normal"
            size="small"
          />
          <TextField
            fullWidth
            label="Email"
            placeholder="Enter email address"
            type="email"
            value={adminForm.email}
            onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
            margin="normal"
            size="small"
          />
          <TextField
            fullWidth
            label="Phone Number"
            placeholder="Enter phone number"
            value={adminForm.phoneNumber}
            onChange={(e) => setAdminForm({ ...adminForm, phoneNumber: e.target.value })}
            margin="normal"
            size="small"
          />
          <TextField
            fullWidth
            label="Password"
            placeholder="Enter password"
            type="password"
            value={adminForm.password}
            onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
            margin="normal"
            size="small"
          />
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>Role</InputLabel>
            <Select
              value={adminForm.role}
              label="Role"
              onChange={(e) => setAdminForm({ ...adminForm, role: e.target.value })}
            >
              <MenuItem value="moderator">Moderator</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOpenAdminDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateAdmin}
            disabled={adminCreating}
            startIcon={adminCreating ? <CircularProgress size={20} /> : <PersonAddIcon />}
          >
            {adminCreating ? 'Creating...' : 'Create Admin'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
